import type { DrawingEntity, Point, PolylineEntity, TextEntity } from '../contract/types.js';

/**
 * The layer outlines are drawn on, and it is a layer so that it can be turned
 * off. A CAD user who has the font wants the real text and none of this; a
 * viewer that has no font shows both and the outlines are what they see.
 */
export const TEXT_OUTLINE_LAYER = 'TEXT_OUTLINE';

/**
 * Turning a string into line-work, because some readers can draw nothing else.
 *
 * **Why this exists at all.** A DXF names a font and cannot carry one, which
 * ADR 0008 records. Finding 8 goes further and is the reason for this file: there
 * is a class of reader that never *reads* the name. Its font is chosen from the
 * interface language before the file is opened, and every character it has no
 * glyph for resolves to `?`. For that reader the six-font choice this writer
 * offers is not a wrong default, it is inert — and the only representation it can
 * draw is the one it already draws for every other line on the sheet.
 *
 * So each string can be emitted a second time as closed contours. **A second
 * time, not instead**: the real `TEXT` entities stay in the file, because text
 * that cannot be selected, searched or re-set is most of the reason a customer
 * asked for DXF rather than an image.
 *
 * This module is pure and knows nothing about fonts, files or the network. It is
 * handed contours in em units and returns polylines in the document's own
 * coordinate space; where those contours come from is `GlyphSource`'s problem,
 * which is what lets Node read a file, the browser fetch one, and these tests use
 * a triangle.
 */

/**
 * One glyph's outlines, in em units, **y-DOWN like the page**.
 *
 * A font's own tables are y-UP — the baseline is 0 and the glyph grows towards
 * increasing y — and this interface deliberately is not, because the one
 * implementation that matters already converts. `opentype.js`'s `getPath`
 * returns screen-space coordinates, so a capital A comes back spanning y −733
 * to 0 rather than 0 to 733.
 *
 * **That was discovered by drawing it, after a unit test agreed with the code
 * that it was y-UP and both were wrong.** The test used a fake font, so it only
 * ever asserted that this file's assumptions agreed with themselves; the
 * rendered text came out mirrored about its own baseline. It is the raster
 * register's R1 in a different file — a rigid transform cannot be validated by
 * algebra, only against the drawing.
 *
 * Stating the convention here rather than flipping downstream means there is one
 * place to be wrong instead of two, and `glyph-source.test.ts` pins it against
 * the real font.
 */
export interface GlyphContours {
  /** Closed contours. The closing point is implied, not repeated. */
  readonly contours: readonly (readonly Point[])[];
  /** How far the pen advances after drawing it, in em units. */
  readonly advance: number;
}

/**
 * Where outlines come from — a port, for the same reason `Detector` is one.
 *
 * The accuracy claim has to name what produced it, and a font is an input like
 * any other: "this string was outlined with this font at this version" is
 * checkable, while "the text was outlined" is not.
 */
export interface GlyphSource {
  /** Named, because the output depends on which font drew it. */
  readonly name: string;
  /** The em square those contours are expressed in, typically 1000 or 2048. */
  readonly unitsPerEm: number;
  /** Contours for one character, or null when the font does not cover it. */
  outline(character: string): GlyphContours | null;
}

export interface OutlinedText {
  /** Closed polylines in the document's coordinate space. */
  readonly polylines: readonly (readonly Point[])[];
  /**
   * Characters the font could not supply, each once.
   *
   * Never silently dropped: the subset this ships with covers the whole of
   * JIS X 0208, which is a bounded claim rather than a complete one, and a
   * character outside it is a quality issue like any other unrecognised mark.
   */
  readonly missing: readonly string[];
}

export function outlineText(entity: TextEntity, source: GlyphSource): OutlinedText {
  const characters = [...entity.text];
  if (characters.length === 0) return { polylines: [], missing: [] };

  const missing: string[] = [];
  const scale = entity.height / source.unitsPerEm;

  // Laid out first in an unrotated frame whose origin is the anchor and whose
  // x runs along the baseline, then placed. Doing it in one pass would mean
  // rotating each glyph as it is drawn, and the run cannot be measured until it
  // has been laid out.
  const laid: Point[][] = [];
  let pen = 0;
  let widest = 0;
  let line = 0;
  for (const character of characters) {
    // A line break is not a character a font failed to supply. MTEXT carries
    // them, and treating one as missing puts noise into a quality report whose
    // only value is being believed — while running the second line on after the
    // first draws a sentence the drawing does not contain.
    if (character === '\n') {
      widest = Math.max(widest, pen);
      pen = 0;
      line += 1;
      continue;
    }
    // Other control characters carry no mark and no width. Silently passed over
    // rather than reported, for the same reason.
    if (character === '\r' || character === '\t' || character === '\u000b') continue;

    const glyph = source.outline(character);
    if (glyph === null) {
      if (!missing.includes(character)) missing.push(character);
      continue;
    }
    // One line height per break, downwards. This is a fallback rendering rather
    // than a typesetter: the contract gives a height and no leading, so a line
    // is exactly its own height below the one above it.
    const drop = line * entity.height;
    for (const contour of glyph.contours) {
      if (contour.length < 2) continue;
      laid.push(contour.map((p) => ({ x: pen + p.x * scale, y: p.y * scale + drop })));
    }
    pen += glyph.advance * scale;
  }
  widest = Math.max(widest, pen);
  if (laid.length === 0) return { polylines: [], missing };

  /**
   * The run is scaled to the extent the extractor measured, and U3 is why.
   *
   * These contours come from a font that is NOT the one the PDF used — the PDF's
   * is not embedded, which is the whole reason this file exists — so its natural
   * advances are the wrong width by construction. The first-use register records
   * what that costs: the overlay drew text at the viewer font's natural width,
   * drifted up to 40% off the page beneath it, and made a conversion correct to
   * 0.2 mm read as broken.
   *
   * Only x is scaled. The height is the entity's own and is already right, and
   * scaling y too would make a glyph that is 40% wider also 40% taller.
   */
  const [minX, , maxX] = entity.source.bbox;
  const measured = maxX - minX;
  // The widest line, not the last one: a two-line block whose second line is
  // shorter would otherwise be stretched to the measured width by the wrong
  // divisor and come out too wide.
  const natural = widest;
  const stretch = measured > 0 && natural > 0 ? measured / natural : 1;

  const radians = (entity.rotation * Math.PI) / 180;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);

  // No y flip. `GlyphContours` is y-down by contract, for the reason its own
  // documentation gives, so the glyph arrives in the page's orientation and
  // flipping here would mirror it about its baseline — which is exactly what
  // this code did until it was drawn and looked at.
  //
  // Rotation is counter-clockwise as the contract states, which in a y-down
  // frame means the sign of the sine terms is the mirror of the textbook pair.
  const place = (p: Point): Point => {
    const x = p.x * stretch;
    const y = p.y;
    return {
      x: entity.anchor.x + x * cos + y * sin,
      y: entity.anchor.y - x * sin + y * cos,
    };
  };

  const polylines = laid.map((contour) => {
    const placed = contour.map(place);
    // Closed explicitly rather than by a flag: these are emitted as polylines
    // and a reader that ignores the closed flag would draw an open glyph.
    return [...placed, placed[0]!];
  });

  return { polylines, missing };
}

/**
 * Every string in a document, as polylines, kept out of `page.entities`.
 *
 * **Out of the entity list on purpose**, and ADR 0008 named the cost of the
 * alternative: `scripts/acceptance.ts` scores what the document contains, so
 * putting twenty contours where one `TEXT` was counts twenty false positives and
 * takes a gate from passing to meaningless. These are a rendering aid for a
 * reader that cannot draw text, not a claim about what was extracted, and the
 * measurement layer must never see them.
 *
 * The polylines come back in **page space**, the same space `page.entities` are
 * in, so the writer moves them with `toDrawingSpace` exactly as it moves
 * everything else. Sharing that one transform is what keeps the outlines on top
 * of the text they stand for instead of near it.
 */
export interface DocumentOutlines {
  /** Keyed by page index. A page with no text does not appear. */
  readonly byPage: ReadonlyMap<number, readonly PolylineEntity[]>;
  /** Characters no glyph was available for, each once, across the document. */
  readonly missing: readonly string[];
  /** Which font drew them, because the outlines depend on it. */
  readonly fontName: string;
}

export function outlineDocument(
  doc: { pages: readonly { index: number; entities: readonly DrawingEntity[] }[] },
  source: GlyphSource,
): DocumentOutlines {
  const byPage = new Map<number, PolylineEntity[]>();
  const missing: string[] = [];

  for (const page of doc.pages) {
    const polylines: PolylineEntity[] = [];
    for (const entity of page.entities) {
      if (entity.type !== 'TEXT' && entity.type !== 'MTEXT') continue;
      const outlined = outlineText(entity, source);
      for (const character of outlined.missing) {
        if (!missing.includes(character)) missing.push(character);
      }
      outlined.polylines.forEach((contour, index) => {
        polylines.push({
          // Derived from the text's own identifier, so a contour can be traced
          // back to the string it came from rather than being anonymous ink.
          id: `${entity.id}-o${String(index).padStart(3, '0')}`,
          type: 'LWPOLYLINE',
          layer: TEXT_OUTLINE_LAYER,
          strokeWidth: entity.strokeWidth,
          colour: entity.colour,
          origin: entity.origin,
          vertices: [...contour],
          closed: true,
          // Straight everywhere: a flattened curve is a chord run, and inventing
          // a bulge would be drawing a shape the font did not describe.
          bulges: contour.map(() => 0),
          source: entity.source,
        });
      });
    }
    if (polylines.length > 0) byPage.set(page.index, polylines);
  }

  return { byPage, missing, fontName: source.name };
}
